import { useState } from 'react';
import { DataTableRenderCellParams } from '@/components/custom/erp';

export interface SelectedCellInfo {
  id: string;
  field: string;
}

// Định nghĩa interface cơ bản cho các row với uuid
export interface BaseRow {
  uuid?: string;
  [key: string]: unknown;
}

// Định nghĩa kiểu cho hàm tạo dòng mới
export type CreateRowFn<T extends BaseRow> = () => T;

// Sử dụng generic type T để cho phép truyền kiểu dữ liệu từ component khác
export default function useInputTableRow<T extends BaseRow>(
  initialRows: T[] = [] as T[],
  createRowFn?: CreateRowFn<T>
) {
  const [rows, setRows] = useState<T[]>(initialRows);
  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null | undefined>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);

  // Hàm mặc định để tạo dòng mới nếu không có hàm tạo được truyền vào
  const defaultCreateRow = (): T => {
    // Tạo một đối tượng cơ bản chỉ với uuid
    const baseRow: BaseRow = {};
    return baseRow as unknown as T;
  };

  // Sử dụng hàm tạo dòng được truyền vào hoặc hàm mặc định
  const createNewRow = createRowFn || defaultCreateRow;

  const handleAddRow = () => {
    const newRow = createNewRow();
    setRows([...rows, newRow]);

    // Select the newly added row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleDeleteRow = () => {
    let updatedRows: T[] = [];

    if (selectedRowUuid) {
      updatedRows = rows.filter(row => row.uuid !== selectedRowUuid);
      setRows(updatedRows);
    } else if (rows.length > 0) {
      updatedRows = rows.slice(0, -1);
      setRows(updatedRows);
    }

    if (updatedRows.length > 0) {
      const lastRow = updatedRows[updatedRows.length - 1];
      setSelectedRowUuid(lastRow.uuid || null);
      setSelectedRow(lastRow);
    } else {
      clearSelection();
    }
  };

  const handleCopyRow = () => {
    if (!selectedRow) return;

    // Tạo một bản sao của dòng đã chọn với uuid mới
    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    // Tạo dòng mới bằng cách kết hợp dữ liệu từ dòng đã chọn
    const baseNewRow = createNewRow();
    const newRow = {
      ...baseNewRow,
      ...rowDataWithoutUuid
    };

    setRows([...rows, newRow]);

    // Select the newly copied row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handlePasteRow = () => {
    if (!selectedRow) return;

    // Tạo một bản sao của dòng đã chọn với uuid mới
    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    // Tạo dòng mới bằng cách kết hợp dữ liệu từ dòng đã chọn
    const baseNewRow = createNewRow();
    const newRow = {
      ...baseNewRow,
      ...rowDataWithoutUuid
    };

    if (selectedRowUuid) {
      const selectedIndex = rows.findIndex(row => row.uuid === selectedRowUuid);
      if (selectedIndex === -1) return;

      const newRows = [...rows];
      newRows.splice(selectedIndex + 1, 0, newRow);

      setRows(newRows);
    } else {
      setRows([...rows, newRow]);
    }

    // Select the newly pasted row
    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleMoveRow = (direction: 'up' | 'down') => {
    if (!selectedRowUuid) return;

    const currentIndex = rows.findIndex(row => row.uuid === selectedRowUuid);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(rows.length - 1, currentIndex + 1);

    if (newIndex === currentIndex) return;

    const newRows = [...rows];
    const [movedRow] = newRows.splice(currentIndex, 1);
    newRows.splice(newIndex, 0, movedRow);

    setRows(newRows);

    // Ensure selection follows the moved row
    // No need to update selectedRowUuid as it remains the same
    setSelectedRow(movedRow);
  };

  const handleCellValueChange = (rowUuid: string, field: string, newValue: unknown) => {
    const rowIndex = rows.findIndex(row => row.uuid === rowUuid);
    if (rowIndex === -1) return;

    const updatedRows = [...rows];
    updatedRows[rowIndex] = {
      ...updatedRows[rowIndex],
      [field]: newValue
    };

    setRows(updatedRows);

    // If the changed row is the currently selected row, update selectedRow
    if (rowUuid === selectedRowUuid) {
      setSelectedRow(updatedRows[rowIndex]);
    }
  };

  const handleRowClick = (params: { id: string; row: T }) => {
    const rowUuid = params.id || params.row?.uuid;
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row);
  };

  const handleCellClick = (params: DataTableRenderCellParams<T>) => {
    const rowUuid = params.id.toString();
    if (!rowUuid) return;

    // Update row selection
    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as T);

    // Update cell selection
    setSelectedCell({
      id: rowUuid,
      field: params.field
    });
  };

  const clearSelection = () => {
    setSelectedRowUuid(null);
    setSelectedRow(null);
    setSelectedCell(null);
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  };
}
